/**
 * <ai-smooth-scroll>
 *
 * A custom element that intercepts anchor link clicks and smooth-scrolls
 * to the target section. Follows @tailwindplus/elements conventions:
 * light DOM only, declarative attributes, progressive enhancement.
 *
 * Usage:
 *   <ai-smooth-scroll>
 *     <a href="#features">Features</a>
 *     <a href="#pricing">Pricing</a>
 *   </ai-smooth-scroll>
 *
 * Attributes:
 *   selector  - CSS selector for links to enhance (default: 'a[href^="#"]')
 *   duration  - Scroll duration hint in ms, used for the post-scroll dialog
 *               close delay (default: 300). Actual scroll speed is browser-controlled.
 *
 * Fallback:
 *   JS disabled → <ai-smooth-scroll> is ignored, links work as normal anchors.
 *   prefers-reduced-motion: reduce → instant scroll (no animation).
 */
class AiSmoothScroll extends HTMLElement {
  #links = []
  #handleClick = null

  static get observedAttributes() {
    return ["selector"]
  }

  get selector() {
    return this.getAttribute("selector") || 'a[href^="#"]'
  }

  get duration() {
    return parseInt(this.getAttribute("duration") || "300", 10)
  }

  connectedCallback() {
    this.#handleClick = this.#onClick.bind(this)
    this.#bind()
  }

  disconnectedCallback() {
    this.#unbind()
  }

  attributeChangedCallback() {
    if (this.isConnected) {
      this.#unbind()
      this.#bind()
    }
  }

  #bind() {
    this.#links = [...this.querySelectorAll(this.selector)]
    for (const link of this.#links) {
      link.addEventListener("click", this.#handleClick)
    }
  }

  #unbind() {
    for (const link of this.#links) {
      link.removeEventListener("click", this.#handleClick)
    }
    this.#links = []
  }

  #onClick(event) {
    const link = event.currentTarget
    const hash = link.getAttribute("href")
    if (!hash || !hash.startsWith("#")) return

    event.preventDefault()

    // Respect prefers-reduced-motion
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches
    const behavior = prefersReducedMotion ? "instant" : "smooth"

    // href="#" means scroll to top
    if (hash === "#") {
      window.scrollTo({ top: 0, behavior })
      history.pushState(null, "", window.location.pathname)
    } else {
      const target = document.getElementById(hash.slice(1))
      if (!target) return

      // Calculate scroll position manually to reliably account for the
      // sticky header. scrollIntoView + scroll-padding-top is inconsistent
      // across browsers, especially with custom font-size on <html>.
      const scrollPadding = parseFloat(
        getComputedStyle(document.documentElement).scrollPaddingTop
      ) || 0
      const targetTop = target.getBoundingClientRect().top + window.scrollY - scrollPadding

      window.scrollTo({ top: targetTop, behavior })
      history.pushState(null, "", hash)
    }

    // If inside an open <dialog>, close it after scroll starts
    const dialog = link.closest("dialog[open]")
    if (dialog) {
      setTimeout(() => dialog.close(), prefersReducedMotion ? 0 : this.duration)
    }
  }
}

customElements.define("ai-smooth-scroll", AiSmoothScroll)
